import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const ModuleConfig = () => {
  const { modules, updateModules } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [draggedModule, setDraggedModule] = useState(null);
  const [dragOverModule, setDragOverModule] = useState(null);

  const moduleDefinitions = {
    charts: {
      name: 'Charts Module',
      description: 'Display various chart types with dynamic data',
      icon: '📊',
      settings: [
        { key: 'count', label: 'Number of Charts', type: 'number', min: 1, max: 10 },
        { key: 'data', label: 'Data Points', type: 'array', description: 'Comma-separated values' }
      ]
    },
    analytics: {
      name: 'Analytics Module',
      description: 'Show user engagement and metrics',
      icon: '📈',
      settings: [
        { key: 'count', label: 'Number of Metrics', type: 'number', min: 1, max: 5 },
        { key: 'data', label: 'Metric Values', type: 'array', description: 'Comma-separated values' }
      ]
    },
    users: {
      name: 'Users Module',
      description: 'User management and activity tracking',
      icon: '👥',
      settings: [
        { key: 'count', label: 'Number of Users', type: 'number', min: 1, max: 20 },
        { key: 'data', label: 'User Data', type: 'array', description: 'Comma-separated user IDs' }
      ]
    }
  };

  const handleDragStart = (e, moduleKey) => {
    setDraggedModule(moduleKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, moduleKey) => {
    e.preventDefault();
    setDragOverModule(moduleKey);
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    if (draggedModule && dragOverModule && draggedModule !== dragOverModule) {
      // Reorder modules logic would go here
      // For now, we'll just show a visual indicator
      console.log(`Would move ${draggedModule} to position of ${dragOverModule}`);
    }
    setDraggedModule(null);
    setDragOverModule(null);
  };

  const handleDrop = (e, targetModule) => {
    e.preventDefault();
    if (draggedModule && draggedModule !== targetModule) {
      // This is where you'd implement the reordering logic
      // For demo purposes, we'll just log it
      console.log(`Dropped ${draggedModule} on ${targetModule}`);
    }
    setDraggedModule(null);
    setDragOverModule(null);
  };

  const handleModuleToggle = (moduleName) => {
    updateModules(moduleName, {
      enabled: !modules[moduleName]?.enabled
    });
  };

  const handleSettingChange = (moduleName, settingKey, value) => {
    updateModules(moduleName, {
      [settingKey]: value
    });
  };

  const parseArrayInput = (input) => {
    if (!input) return [];
    return input.split(',').map(item => {
      const num = parseInt(item.trim());
      return isNaN(num) ? item.trim() : num;
    }).filter(item => item !== '');
  };

  return (
    <div className="space-y-4">
      {Object.entries(moduleDefinitions).map(([key, module]) => {
        const moduleConfig = modules[key];
        const isEnabled = moduleConfig?.enabled || false;
        const isBeingDragged = draggedModule === key;
        const isDragOver = dragOverModule === key;

        return (
          <div 
            key={key}
            className={`theme-bg rounded-lg shadow-md border p-4 transition-all duration-200 ${
              isBeingDragged ? 'opacity-50 scale-95' : ''
            } ${isDragOver ? 'ring-2 ring-primary-400' : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, key)}
            onDragOver={(e) => handleDragOver(e, key)}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, key)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{module.icon}</span>
                <div>
                  <h3 className="font-semibold theme-primary">{module.name}</h3>
                  <p className="text-sm theme-secondary">{module.description}</p>
                  {isBeingDragged && (
                    <span className="text-xs text-primary-600 ml-2">Dragging...</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleModuleToggle(key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isEnabled ? 'theme-accent' : 'theme-bg-tertiary'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                    isEnabled ? 'translate-x-6 theme-bg' : 'translate-x-1 theme-bg'
                  }`}
                />
              </button>
            </div>

            {isEnabled && (
              <div className="space-y-3 mt-4 pt-4 border-t theme-border-secondary">
                {module.settings.map((setting) => {
                  const currentValue = moduleConfig?.[setting.key];
                  
                  if (setting.type === 'number') {
                    return (
                      <div key={setting.key} className="flex items-center justify-between">
                        <label className="text-sm font-medium theme-primary">
                          {setting.label}
                        </label>
                        <input
                          type="number"
                          min={setting.min}
                          max={setting.max}
                          value={currentValue || setting.min}
                          onChange={(e) => handleSettingChange(key, setting.key, parseInt(e.target.value))}
                          className="w-20 px-2 py-1 border theme-border rounded-md text-sm focus:ring-theme-accent focus:border-theme-accent"
                        />
                        {setting.description && (
                          <p className="text-xs theme-secondary ml-2">{setting.description}</p>
                        )}
                      </div>
                    );
                  }

                  if (setting.type === 'array') {
                    const arrayValue = Array.isArray(currentValue) ? currentValue.join(', ') : '';
                    return (
                      <div key={setting.key} className="space-y-2">
                        <label className="text-sm font-medium theme-primary">
                          {setting.label}
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={arrayValue}
                            onChange={(e) => handleSettingChange(key, setting.key, parseArrayInput(e.target.value))}
                            placeholder={setting.description}
                            className="flex-1 px-2 py-1 border theme-border rounded-md text-sm focus:ring-theme-accent focus:border-theme-accent"
                          />
                          <div className="flex flex-col space-y-1">
                            <span className="text-xs theme-secondary">Drag values to reorder</span>
                            <div className="flex flex-wrap gap-1">
                              {(parseArrayInput(arrayValue)).map((val, idx) => (
                                <span 
                                  key={idx}
                                  className="px-2 py-1 theme-bg-secondary rounded text-xs cursor-move hover:theme-bg-tertiary"
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.setData('text/plain', val.toString());
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                >
                                  {val}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ModuleConfig;
